import { Edit } from '@mui/icons-material'
import { Button, Card, CardContent, CircularProgress, Divider, Grid, Typography } from '@mui/material'
import { GreenText, LabelField } from 'components'
import { VehicleDto, VehicleIncorrectMileageResolutionDto } from 'dtos'
import { formatDate } from 'utils'
import ClusterIncorrectMileageResolutionEditDialog from './ClusterIncorrectMileageResolutionEditDialog'
import { useEffect, useState } from 'react'
import * as controllers from 'controllers'
import { enqueueSnackbar } from 'notistack'
import { useAuth } from 'context'

interface ClusterIncorrectMileageResolutionProps {
  vehicle: VehicleDto
}

export default function ClusterIncorrectMileageResolution({ vehicle }: ClusterIncorrectMileageResolutionProps) {
  const { permissions } = useAuth()
  const [clusterIncorrectMileageResolutionEditDialogOpen, setClusterIncorrectMileageResolutionEditDialogOpen] =
    useState<boolean>(false)

  const [isSavingClusterIncorrectMileageResolution, setIsSavingClusterIncorrectMileageResolution] = useState<boolean>(false)
  const [isGettingClusterIncorrectMileageResolution, setIsGettingClusterIncorrectMileageResolution] = useState<boolean>(false)

  const [incorrectMileageResolution, setIncorrectMileageResolution] = useState<VehicleIncorrectMileageResolutionDto>(
    new VehicleIncorrectMileageResolutionDto()
  )

  useEffect(() => {
    if (vehicle.vehicleGuid) {
      setIsGettingClusterIncorrectMileageResolution(true)
      controllers
        .getIncorrectMileageResolution(vehicle.vehicleGuid)
        .then(response => {
          setIncorrectMileageResolution(response.value)
        })
        .finally(() => {
          setIsGettingClusterIncorrectMileageResolution(false)
        })
    }
  }, [vehicle])

  return (
    <>
      {permissions.CLUSTERS_ADD_EDIT && (
        <ClusterIncorrectMileageResolutionEditDialog
          initialValues={incorrectMileageResolution}
          isLoading={isSavingClusterIncorrectMileageResolution}
          onClose={() => {
            setClusterIncorrectMileageResolutionEditDialogOpen(false)
          }}
          onSave={values => {
            setIsSavingClusterIncorrectMileageResolution(true)
            controllers
              .updateIncorrectMileageResolution(vehicle.vehicleGuid!, values)
              .then(response => {
                enqueueSnackbar('Incorrect Mileage Resolution Updated', { variant: 'success' })
                setIncorrectMileageResolution(response.value)
                setClusterIncorrectMileageResolutionEditDialogOpen(false)
              })
              .finally(() => {
                setIsSavingClusterIncorrectMileageResolution(false)
              })
          }}
          open={clusterIncorrectMileageResolutionEditDialogOpen}
        />
      )}

      <Card variant='outlined'>
        <CardContent>
          <Grid container spacing={1} alignItems='flex-start'>
            <Grid item xs={12} container alignItems='center' justifyContent='space-between'>
              <Grid item xs={12} sm={6}>
                <Typography fontWeight='bold' variant='h5'>
                  Incorrect Mileage Resolution
                </Typography>
              </Grid>

              <Grid item xs={12} sm='auto'>
                {permissions.CLUSTERS_ADD_EDIT && (
                  <Button
                    onClick={() => {
                      setClusterIncorrectMileageResolutionEditDialogOpen(true)
                    }}
                    variant='text'
                    endIcon={<Edit />}
                    disabled={isSavingClusterIncorrectMileageResolution}
                  >
                    EDIT
                  </Button>
                )}
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            {isGettingClusterIncorrectMileageResolution && (
              <Grid item container alignItems='center' justifyContent='center' sx={{ my: 1 }}>
                <Grid item>
                  <CircularProgress color='primary' size={50} />
                </Grid>
              </Grid>
            )}

            {!isGettingClusterIncorrectMileageResolution && (
              <>
                <Grid item xs={12}>
                  <LabelField
                    label='Needs Incorrect Mileage Resolution'
                    value={
                      incorrectMileageResolution.needsIncorrectMileageResolutionDateTime
                        ? `${formatDate(incorrectMileageResolution.needsIncorrectMileageResolutionDateTime)} by ${
                            incorrectMileageResolution.needsIncorrectMileageResolutionByPerson?.firstName
                          } ${incorrectMileageResolution.needsIncorrectMileageResolutionByPerson?.lastName}`
                        : ''
                    }
                  />
                </Grid>

                <Grid item xs={12}>
                  <LabelField
                    label='Incorrect Mileage Resolved'
                    value={
                      incorrectMileageResolution.incorrectMileageResolvedDateTime
                        ? `${formatDate(incorrectMileageResolution.needsIncorrectMileageResolutionDateTime)} by ${
                            incorrectMileageResolution.needsIncorrectMileageResolutionByPerson?.firstName
                          } ${incorrectMileageResolution.needsIncorrectMileageResolutionByPerson?.lastName}`
                        : ''
                    }
                  />
                </Grid>
              </>
            )}

            <Grid item xs={12}>
              <GreenText variant='outlined'>
                {`**EDIT Button:**

Clicking the EDIT button opens the Incorrect Mileage Resolution dialog. 

The EDIT button may only be pressed by users with “Cluster Admin” user permission. 

The EDIT button is disabled for all users when the “Current Odometer Reading (After Cluster Reinstallation Complete)” field on the Cluster Information card contains a value. 

**Needs Incorrect Mileage Resolution (***Coming in Future Release***):**

Displays the date this was set and the user who began the cluster reinstallation when the incorrect mileage was detected.

If no value has been entered, a “-” will be displayed.

**Incorrect Mileage Resolved (***Coming in Future Release***):**

Displays the date this was set and the administrative user who resolved the incorrect mileage.

If no value has been entered, a “-” will be displayed.`}
              </GreenText>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </>
  )
}
